import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export interface Supplier {
  id: string;
  user_id: string;
  name: string;
  official_name?: string;
  logo_url?: string;
  website?: string;
  contact_info: any;
  detection_patterns: any;
  confidence_threshold: number;
  processing_template_id?: string;
  is_active: boolean;
  auto_detected_count: number;
  manual_confirmed_count: number;
  accuracy_score: number;
  created_at: string;
  updated_at: string;
  last_detection_at?: string;
  total_bills_processed?: number;
  avg_processing_time_ms?: number;
  error_rate?: number;
  last_error_at?: string;
  performance_score?: number;
  api_integration_config?: any;
  parent_supplier_id?: string;
  hierarchy_level?: number;
  tags?: string[];
  processing_priority?: number;
}

export interface CreateSupplierData {
  name: string;
  official_name?: string;
  logo_url?: string;
  website?: string;
  contact_info?: any;
  detection_patterns?: any;
  confidence_threshold?: number;
  processing_template_id?: string;
  is_active?: boolean;
}

export interface UpdateSupplierData extends Partial<CreateSupplierData> {
  id: string;
}

export const useSuppliers = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const suppliersQuery = useQuery({
    queryKey: ['suppliers', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('user_id', user?.id)
        .order('name');

      if (error) throw error;
      return data as Supplier[];
    },
    enabled: !!user?.id,
  });

  const createSupplierMutation = useMutation({
    mutationFn: async (newSupplier: CreateSupplierData) => {
      const { data, error } = await supabase
        .from('suppliers')
        .insert({
          ...newSupplier,
          user_id: user?.id,
          detection_patterns: newSupplier.detection_patterns || {},
          confidence_threshold: newSupplier.confidence_threshold || 0.8,
          contact_info: newSupplier.contact_info || {}
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast({ title: "Success", description: "Supplier created successfully" });
    },
    onError: (error) => {
      console.error('Error creating supplier:', error);
      toast({ title: "Error", description: "Failed to create supplier", variant: "destructive" });
    },
  });

  const updateSupplierMutation = useMutation({
    mutationFn: async ({ id, ...updates }: UpdateSupplierData) => {
      const { data, error } = await supabase
        .from('suppliers')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user?.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast({ title: "Success", description: "Supplier updated successfully" });
    },
    onError: (error) => {
      console.error('Error updating supplier:', error);
      toast({ title: "Error", description: "Failed to update supplier", variant: "destructive" });
    },
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: async (supplierId: string) => {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', supplierId)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast({ title: "Success", description: "Supplier deleted successfully" });
    },
    onError: (error) => {
      console.error('Error deleting supplier:', error);
      toast({ title: "Error", description: "Failed to delete supplier", variant: "destructive" });
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ ids, updates }: { ids: string[], updates: Partial<CreateSupplierData> & { is_active?: boolean } }) => {
      const { data, error } = await supabase
        .from('suppliers')
        .update(updates)
        .in('id', ids)
        .eq('user_id', user?.id)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast({ title: "Success", description: "Suppliers updated successfully" });
    },
    onError: (error) => {
      console.error('Error updating suppliers:', error);
      toast({ title: "Error", description: "Failed to update suppliers", variant: "destructive" });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .in('id', ids)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast({ title: "Success", description: "Suppliers deleted successfully" });
    },
    onError: (error) => {
      console.error('Error deleting suppliers:', error);
      toast({ title: "Error", description: "Failed to delete suppliers", variant: "destructive" });
    },
  });

  const importSuppliersMutation = useMutation({
    mutationFn: async (supplierData: CreateSupplierData[]) => {
      const suppliersWithUser = supplierData.map(supplier => ({
        ...supplier,
        user_id: user?.id,
        detection_patterns: supplier.detection_patterns || {},
        confidence_threshold: supplier.confidence_threshold || 0.8,
        contact_info: supplier.contact_info || {}
      }));

      const { data, error } = await supabase
        .from('suppliers')
        .insert(suppliersWithUser)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast({ title: "Success", description: `${data.length} suppliers imported successfully` });
    },
    onError: (error) => {
      console.error('Error importing suppliers:', error);
      toast({ title: "Error", description: "Failed to import suppliers", variant: "destructive" });
    },
  });

  return {
    suppliers: suppliersQuery.data || [],
    isLoading: suppliersQuery.isLoading,
    error: suppliersQuery.error,
    createSupplier: createSupplierMutation.mutate,
    updateSupplier: updateSupplierMutation.mutate,
    deleteSupplier: deleteSupplierMutation.mutate,
    bulkUpdate: bulkUpdateMutation.mutate,
    bulkDelete: bulkDeleteMutation.mutate,
    importSuppliers: importSuppliersMutation.mutate,
    isCreating: createSupplierMutation.isPending,
    isUpdating: updateSupplierMutation.isPending,
    isDeleting: deleteSupplierMutation.isPending,
    isBulkUpdating: bulkUpdateMutation.isPending,
    isBulkDeleting: bulkDeleteMutation.isPending,
    isImporting: importSuppliersMutation.isPending,
  };
};

export const useSupplierDetectionLogs = (billId?: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['supplier-detection-logs', billId, user?.id],
    queryFn: async () => {
      let query = supabase
        .from('supplier_detection_logs')
        .select(`
          *,
          supplier:suppliers(name, official_name)
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (billId) {
        query = query.eq('bill_id', billId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
};